<template>
    <div class="columns wrapper-transaction-create is-multiline">
      <div class="column is-full" v-if="!canInviteCarriers">
        <p class="content notification is-danger">
          Because a carrier has already started work on this load, you are not able to save any changes to it
        </p>
      </div>
      <b-loading :active="isFetchingInitialData" :is-full-page="true" />
      <div class="column transaction-flow-wrapper">
        <div v-for="section in sections" class="transaction-flow-section" :key="section.key">
          <!-- TODO: would be great to turn all of this in to a component -->
          <h3 class="title" :id="section.key">{{ section.title }}</h3>
          <!-- <template v-if="!showSummary[section.key]"> -->
            <!-- the only reason `:desinations` and `:desinations` is being passed in is to make the "<freight>" component happy -->
            <!-- TODO: find a way to conditionally pass in props to dynamic components -->
            <component :key="section.key"
                       :min-date=" section.key === 'destinations' ? minDestinationDate : undefined"
                       :is="section.editView"
                       :can-save="!isSavingDraft"
                       v-model="transaction[section.key]"
                       :companyJobKey="transaction.companyJobKey"
                       :destinations="transaction.destinations"
                       :origins="transaction.origins"
                       @save-hack="saveTransaction"
                       @fetch-hack="fetchSavedTransaction"
            />

        </div>

        <div v-if="transaction.companyJobKey">
          <h3 class="title">Invitations</h3>
          <invite-list :company-job-key="transaction.companyJobKey"  />
        </div>

        <div class="columns footer-controls is-multiline">
          <div class="column is-full" v-if="!canInviteCarriers">
            <p class="content notification is-danger">
              Because a carrier has already started work on this load, you are not able to save any changes to it
            </p>
          </div>
          <div class="column is-full" v-if="!canCreateTransaction">
            <p class="content notification is-info">
              You must have a date and address for both pick-up and drop-off locations before you can start your shipment.
            </p>
          </div>
          <div class="column is-one-quarter">
            <button type="button" class="button is-fullwidth" @click="toggleShowDeleteConfirmation" :disabled="isSavingDraft">Delete Transaction</button>
          </div>
          <div class="column is-one-third">
            <button type="button" @click="saveAndExit" class="button is-success is-fullwidth"  :class="{ 'is-loading': isSavingDraft }" :disabled="!canInviteCarriers || isSavingDraft" >
              Save changes and return to dashboard
            </button>
          </div>
          <div class="column">
            <button class="button is-primary is-fullwidth"  @click="toggleShowStartTransactionModal" :disabled="!canInviteCarriers || isSavingDraft || !canCreateTransaction">
              Invite carriers and start your shipment
            </button>
          </div>
        </div>
      </div>

      <delete-confirmation :show.sync="showDeleteConfirmation" :company-job="transaction" @deleteConfirmed="deleteTransaction"  />
      <start-transaction :show.sync="showStartTransactionModal" :transaction-key="transaction.companyJobKey" />
    </div>
</template>

<script>
import { isEmpty, flatten } from 'lodash-es'
import { mapGetters, mapState, mapMutations } from 'vuex'
import { format as formatDate } from 'date-fns'

import CompanyJobApi from '@/api-interface/companyjob'
import BasicInformation from '@/components/TransactionCreationWizard/BasicInformation'
import LocationPicker from '@/components/TransactionCreationWizard/LocationPicker'
import FreightDescriber from '@/components/TransactionCreationWizard/FreightDescriber'
import EquipmentSelection from '@/components/TransactionCreationWizard/EquipmentInformation'
import StartTransaction from '@/components/StartTransaction'
import InviteList from '@/components/CompanyJobInviteList'
import DeleteConfirmation from '@/components/TransactionDeleteConfirmationModal'

import { Toast } from 'buefy/dist/components/toast'
import { Snackbar } from 'buefy/dist/components/snackbar'

import restoreCompanyJob from '@/helpers/restoreCompanyJob'

const sections = [
  {
    title: 'Basic Information',
    key: 'basicInfo',
    editView: 'BasicInformation',
    summaryView: 'BasicInformationSummary'
  },
  {
    title: 'Origin',
    key: 'origins',
    editView: 'LocationPicker',
    summaryView: 'LocationPickerSummary'
  },
  {
    title: 'Destination',
    key: 'destinations',
    editView: 'LocationPicker',
    summaryView: 'LocationPickerSummary'
  },
  {
    title: 'Load Details',
    key: 'freight',
    editView: 'FreightDescriber',
    summaryView: 'FreightDescriberSummary'
  },
  {
    title: 'Equipment Information',
    key: 'equipment',
    editView: 'EquipmentSelection',
    summaryView: 'EquipmentInformationSummary'
  }
]

function isJobPointComplete(locations) {
  const hasAddress = locations.map(
    (origin) =>
      (origin.hasOwnProperty('companyRolodexJobLocationAddresses') &&
        !isEmpty(origin.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressCity) &&
        !isEmpty(origin.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressState) &&
        !isEmpty(origin.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressStreet1) &&
        !isEmpty(origin.companyRolodexJobLocationAddresses.companyRolodexJobLocationAddressZip)) ||
      (origin.hasOwnProperty('address') &&
        !isEmpty(origin.address.facilityName) &&
        !isEmpty(origin.address.street1) &&
        !isEmpty(origin.address.zip))
  )
  const hasDate = locations.map((origin) => !isEmpty(origin.companyJobPointDateEarliest))
  //  everything in the array must be true
  return [...hasAddress, ...hasDate].indexOf(false) === -1
}

export default {
  name: 'Transactions-New',

  async created() {
    this._initTransaction()
  },

  async beforeRouteLeave(to, from, next) {
    // save data before the user exists this page.
    // this helps against stuff like accidentally hitting the back button and loosing all your info
    // if (this.transaction.companyJobKey && !this.shouldDeleteDraftOnExit && this.shouldSaveOnExit) {
    // this.saveTransaction({ showNotification: false })
    // } else if (this.shouldDeleteDraftOnExit) {
    if (this.shouldDeleteDraftOnExit) {
      // if the user has not entered any data... then delete the transaction
      CompanyJobApi.delete(this.transaction.companyJobKey)
    }
    next()
  },

  data() {
    return {
      savingPromise: null,
      isFetchingInitialData: false,
      isSavingDraft: false,
      showStartTransactionModal: false,
      showDeleteConfirmation: false,
      currentSection: null,
      sections,
      shouldSaveOnExit: true,
      transaction: {
        companyJobStatus: 'draft',
        companyJobKey: 0,
        createdByAccountKey: null,
        basicInfo: {
          companyJobId: null,
          rate: null,
          milestones: {},
          documents: []
        },
        origins: [],
        destinations: [],
        freight: [],
        equipment: {},
        companyJobPointFreightMapping: []
      }
    }
  },

  computed: {
    ...mapGetters('user', ['accountInfo']),
    ...mapState(['preSelectedShipTo']),

    /**
     * Flag to indicate if the user has put in any type of info.
     * This can be thought of as a "dirty" flag.
     * @returns {Boolean} true = no data has been entered; false = user has entered data
     */
    shouldDeleteDraftOnExit() {
      return (
        !this.hasBasicInfo &&
        !this.hasDestinationInfo &&
        !this.hasOriginInfo &&
        !this.hasEquipmentInfo &&
        !this.hasFreightInfo
      )
    },

    minDestinationDate() {
      const firstOrigin = this.transaction.origins[0] || {}
      if (firstOrigin.companyJobPointDateEarliest) {
        return formatDate(firstOrigin.companyJobPointDateEarliest, 'YYYY-MM-DD')
      }
    },

    canInviteCarriers() {
      return (
        this.transaction.companyJobStatus.toLowerCase() === 'draft' ||
        this.transaction.companyJobStatus.toLowerCase() === 'invites sent'
      )
    },

    canCreateTransaction() {
      return (
        // this.hasBasicInfo &&
        this.hasOriginInfo && this.hasDestinationInfo
        //this.hasEquipmentInfo &&
        //this.hasFreightInfo
      )
    },

    hasBasicInfo() {
      return this.transaction.basicInfo.rate > 0
    },

    hasOriginInfo() {
      return !isEmpty(this.transaction.origins) && isJobPointComplete(this.transaction.origins)
    },

    hasDestinationInfo() {
      return !isEmpty(this.transaction.destinations) && isJobPointComplete(this.transaction.destinations)
    },

    hasFreightInfo() {
      const hasFreight = !isEmpty(this.transaction.freight)
      const hasOrigin = this.transaction.freight.map((f) => !isEmpty(f.origin))
      const hasDestination = this.transaction.freight.map((f) => !isEmpty(f.destination))
      const hasMaterials = this.transaction.freight.map((f) => !isEmpty(f.materials))
      const hasWeight = this.transaction.freight.map((f) => f.weight > 0)
      const hasPallets = this.transaction.freight.map((f) => f.palletCount > 0)
      const hasClass = this.transaction.freight.map((f) => !isEmpty(f.nmfcClass))
      const hasCategory = this.transaction.freight.map((f) => !isEmpty(f.category))
      return (
        [
          hasFreight,
          ...hasMaterials,
          ...hasWeight,
          ...hasPallets,
          ...hasClass,
          ...hasCategory,
          ...hasOrigin,
          ...hasDestination
        ].indexOf(false) === -1
      )
    },

    hasEquipmentInfo() {
      return (
        !isEmpty(this.transaction.equipment) &&
        !isEmpty(this.transaction.equipment.companyJobTrailerType) &&
        this.transaction.equipment.companyJobInsuranceCoverageMinimum > 0
      )
    },

    transactionPayload() {
      const data = this.transaction

      return {
        createdByAccountKey: data.createdByAccountKey || this.accountInfo.accountKey,
        companyJobKey: data.companyJobKey,
        companyKey: this.accountInfo.company.companyKey,
        companyJobDocuments: data.basicInfo.documents,
        companyJobEquipmentComments: data.equipment.companyJobEquipmentComments,
        companyJobInsuranceCoverageMinimum: data.equipment.companyJobInsuranceCoverageMinimum,
        companyJobRate: data.basicInfo.rate,
        companyJobPointFreightMapping: [],
        companyJobFreights: this.transaction.freight,
        companyJobStrapCount: data.equipment.companyJobStrapCount,
        companyJobTrailerType: data.equipment.companyJobTrailerType,
        companyJobPoints: [
          ...data.destinations.map((dest) => {
            return {
              CompanyRolodexJobLocationAddressKey: dest.CompanyRolodexJobLocationAddressKey,
              companyJobPointKey: dest.companyJobPointKey,
              companyJobPointDateEarliest: dest.companyJobPointDateEarliest,
              companyJobPointDateLatest: dest.companyJobPointDateLatest,
              companyJobPointDock: dest.companyJobPointDock,
              companyJobPointType: 'destination',
              CompanyRolodexContactKey1: dest.CompanyRolodexContactKey1
                ? dest.CompanyRolodexContactKey1.companyRolodexContactKey
                : null,
              CompanyRolodexContactKey2: dest.CompanyRolodexContactKey2
                ? dest.CompanyRolodexContactKey2.companyRolodexContactKey
                : null
            }
          }),
          ...data.origins.map((origin) => {
            return {
              CompanyRolodexJobLocationAddressKey: origin.CompanyRolodexJobLocationAddressKey,
              companyJobPointKey: origin.companyJobPointKey,
              companyJobPointDateEarliest: origin.companyJobPointDateEarliest,
              companyJobPointDateLatest: origin.companyJobPointDateLatest,
              companyJobPointDock: origin.companyJobPointDock,
              companyJobPointType: 'origin',
              CompanyRolodexContactKey1: origin.CompanyRolodexContactKey1
                ? origin.CompanyRolodexContactKey1.companyRolodexContactKey
                : null,
              CompanyRolodexContactKey2: origin.CompanyRolodexContactKey2
                ? origin.CompanyRolodexContactKey2.companyRolodexContactKey
                : null
            }
          })
        ]
      }
    }
  },

  components: {
    BasicInformation,
    LocationPicker,
    FreightDescriber,
    EquipmentSelection,
    StartTransaction,
    InviteList,
    DeleteConfirmation
  },

  methods: {
    ...mapMutations({ setPreselectedLocation: 'SET_PRESELECTED_SHIP_TO' }),
    _initTransaction() {
      this.transaction.companyJobKey = this.$route.params.companyJobKey

      if (this.transaction.companyJobKey) {
        this.fetchSavedTransaction()
      } else {
        // if a pre-selected address is present, populate it before the initial save
        if (this.preSelectedShipTo) {
          this.transaction.origins[0] = {
            CompanyRolodexJobLocationAddressKey: this.preSelectedShipTo.companyRolodexJobLocationAddressKey
          }
          // clear the state of the pre-selected ship-to address
          this.setPreselectedLocation(null)
        }
        this.saveTransaction({ showNotification: false })
        this.currentSection = 'basicInfo'
      }
    },

    generateFreightToPointMapping(stagedFreightData, fromServerFreightData) {
      const nestedArray = stagedFreightData.map((stagedFreight, index) => {
        const matchingServerFreight = fromServerFreightData[index]
        return [
          {
            companyJobFreightKey: matchingServerFreight.companyJobFreightKey,
            companyJobPointKey: stagedFreight.originKey
          },
          {
            companyJobFreightKey: matchingServerFreight.companyJobFreightKey,
            companyJobPointKey: stagedFreight.destinationKey
          }
        ]
      })

      return flatten(nestedArray)
    },

    async fetchSavedTransaction() {
      this.isFetchingInitialData = true
      try {
        const savedData = (await CompanyJobApi.getByKey(this.transaction.companyJobKey)).data
        await this.restoreSavedData(savedData)
      } catch (error) {
        Snackbar.open({
          message: `There was an error while trying to retrieve your draft transaction.
            Please check your information and try again or contact support.
            ${error}`,
          position: 'is-top',
          type: 'is-danger',
          indefinite: true
        })
      } finally {
        this.isFetchingInitialData = false
      }
    },

    async saveTransaction({ showNotification } = { showNotification: true }) {
      if (this.transaction.companyJobKey) {
        this.isSavingDraft = true
        try {
          let updatedData = (await CompanyJobApi.update(this.transactionPayload)).data

          // we need to restore the saved data the first time around so that any system-generated IDs get put back in to the local state

          await this.restoreSavedData(updatedData)

          // this is a really crappy hack to work around the crappy "companyJobPointFreightMapping" in the API
          //if (!isEqual(updatedData.companyJobPointFreightMapping, this.transactionPayload.companyJobPointFreightMapping)) {
          if (this.transaction.freight.length) {
            const mappings = this.generateFreightToPointMapping(
              this.transaction.freight,
              updatedData.companyJobFreights
            )

            const newData = {
              ...updatedData,
              companyJobPointFreightMapping: mappings
            }

            updatedData = (await CompanyJobApi.update(newData)).data
            await this.restoreSavedData(updatedData)
          }
        } catch (error) {
          Snackbar.open({
            message: `There was an error while trying to retrieve your draft transaction.
            Please check your information and try again or contact support.
            ${error}`,
            position: 'is-top',
            type: 'is-danger',
            indefinite: true
          })
        } finally {
          this.isSavingDraft = false
        }
      } else {
        // if no companyJobKey is present... then we need to do the initial save to get get one.
        // we do not want the user to type in any info during the initial save... otherwise it will get lost
        // this also helps guard against "cold" AWS Lambdas, since the initial save could take a long time.
        this.isFetchingInitialData = true
        const response = await CompanyJobApi.create(this.transactionPayload)
        this.isFetchingInitialData = false
        this.transaction.companyJobKey = response.data.companyJobKey
        await this.restoreSavedData(response.data)

        const currentRouteName = this.$route.name
        this.$router.replace({ name: currentRouteName, params: { companyJobKey: this.transaction.companyJobKey } })
      }

      if (showNotification) {
        Toast.open({
          message: 'Current transaction has been saved as a draft.',
          type: 'is-success',
          duration: 4000
        })
      }

      this.isSavingDraft = false
    },

    /**
     * Takes the "raw" CompanyJob object from the server and re-maps everything in to sensible bits that can be used by the UI.
     * Everything gets put in to the "transaction" object. You can think of this as the opposite of the "transactionPayload" computed property
     * @param savedData
     * @returns {Promise<void>}
     */
    async restoreSavedData(savedData) {
      this.transaction = await restoreCompanyJob(savedData)
    },

    toggleShowStartTransactionModal() {
      this.saveTransaction({ showNotification: false })
      this.showStartTransactionModal = !this.showStartTransactionModal
    },

    toggleShowDeleteConfirmation() {
      this.showDeleteConfirmation = !this.showDeleteConfirmation
    },

    /**
     * Delete the current transaction
     * @returns {Promise<void>}
     */
    async deleteTransaction() {
      await CompanyJobApi.delete(this.transaction.companyJobKey)
      this.toggleShowDeleteConfirmation()
      this.shouldSaveOnExit = false
      this.$router.push({ name: 'dashboard' })
    },

    async saveAndExit() {
      await this.saveTransaction({ showNotification: true })
      this.$router.push({ name: 'dashboard' })
    }
  }
}
</script>

<style scoped lang="scss">
.wrapper-transaction-create::after {
  // TODO: this would be great as a mix-in
  content: '';
  background-image: url('../../assets/backgrounds/image003.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  opacity: 0.4;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  position: fixed;
  z-index: -1;
}

.transaction-flow-wrapper {
  margin-bottom: 80px;
  height: 100%;
}

.transaction-flow-section {
  margin: 20px 0;
  padding: 20px 0;
  border-bottom: 1px solid #efefef;
}

.title {
  margin-top: 10px;
  padding-top: 10px;
  // border-top: 1px solid #efefef;
}

.next-btn {
  margin-top: 25px;
  width: 280px;
}

.is-save-draft-btn {
  width: 50%;
}

.footer-controls {
  margin-top: 50px;
}
</style>
